import { ParcelLabelsDialogBody } from "@/components/parcel-labels-dialog-body";
import { MIN_PARCELS, useLabelPrinting } from "@/hooks/use-label-printing";
import { jobsApi } from "@/lib/jobs";

interface Props {
  jobId: number;
  /** Parcel count captured when the job was dispatched; null if never dispatched. */
  dispatchParcelCount: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Prints one 100x150mm parcel label per parcel ("1 of 5", "2 of 5", …) for a job.
 *
 * The count is seeded from the job's dispatch record so the common case — print
 * labels for the parcels you just dispatched — is a single click, but the
 * warehouse can override it (e.g. reprinting a damaged label run for a job that
 * was never formally dispatched). The server validates and clamps the count too.
 *
 * Everything after "which job" — the agent probe, the printer picker, the dispatch branch and
 * the ZPL diagnostic — lives in `useLabelPrinting` and `<ParcelLabelsDialogBody>`, shared with the
 * invoice-screen dialog. What is left here is the job's two API calls and its seeded count.
 */
export function PrintParcelLabelsDialog({ jobId, dispatchParcelCount, open, onOpenChange }: Props) {
  const labels = useLabelPrinting({
    open,
    onOpenChange,
    initialCount: dispatchParcelCount ?? MIN_PARCELS,
    fetchPdf: (parcelCount) => jobsApi.printDeliverySticker(jobId, parcelCount),
    // The job route records a job event for every fetch, so a diagnostic download must say so:
    // recorded as prints they would bury the job's real dispatch history under parcel prints
    // nobody performed.
    fetchZpl: (parcelCount, purpose) =>
      purpose === "download"
        ? jobsApi.printDeliveryStickerZpl(jobId, parcelCount, "download")
        : jobsApi.printDeliveryStickerZpl(jobId, parcelCount),
    downloadFileName: `parcel-labels-${jobId}.zpl`,
  });

  return <ParcelLabelsDialogBody open={open} onOpenChange={onOpenChange} labels={labels} />;
}
